import { useEffect, useRef, useState } from 'react';
import { PaymentsDueService } from '../services/PaymentsDueService';
import type {
  PaymentsDueDashboard,
  PaymentsDueQuery,
} from '../utils/paymentsDueDashboard';

const SEARCH_DEBOUNCE_MS = 350;

function areQueriesEqualExceptSearch(
  left: PaymentsDueQuery,
  right: PaymentsDueQuery,
): boolean {
  return left.page === right.page
    && left.pageSize === right.pageSize
    && left.dueStart === right.dueStart
    && left.dueEnd === right.dueEnd
    && left.overdueMinDays === right.overdueMinDays
    && left.paymentGateway.length === right.paymentGateway.length
    && left.paymentGateway.every((value, index) => value === right.paymentGateway[index]);
}

export function usePaymentsDueDashboard(query: PaymentsDueQuery | null) {
  const [snapshot, setSnapshot] = useState<PaymentsDueDashboard | null>(null);
  const [loading, setLoading] = useState(Boolean(query));
  const [error, setError] = useState<string | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const prevQuery = useRef<PaymentsDueQuery | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    if (!query) {
      clearTimeout(debounceTimer.current);
      prevQuery.current = null;
      setSnapshot(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    const previous = prevQuery.current;
    const onlySearchChanged = Boolean(
      previous
      && query.searchName !== previous.searchName
      && areQueriesEqualExceptSearch(query, previous),
    );
    prevQuery.current = query;

    const run = () => {
      const currentRequest = ++requestId.current;
      setLoading(true);
      setError(null);

      PaymentsDueService.getDashboard(query)
        .then((next) => {
          if (currentRequest !== requestId.current) {
            return;
          }
          setSnapshot(next);
        })
        .catch((caught: unknown) => {
          if (currentRequest !== requestId.current) {
            return;
          }
          setSnapshot(null);
          setError(caught instanceof Error ? caught.message : 'Não foi possível carregar as alunas a vencer.');
        })
        .finally(() => {
          if (currentRequest === requestId.current) {
            setLoading(false);
          }
        });
    };

    clearTimeout(debounceTimer.current);
    if (onlySearchChanged) {
      debounceTimer.current = setTimeout(run, SEARCH_DEBOUNCE_MS);
    } else {
      run();
    }

    return () => {
      clearTimeout(debounceTimer.current);
    };
  }, [query]);

  return { snapshot, loading, error };
}
